import { api } from "./client";
import type {
  ArchiveScope,
  DocumentDto,
  DocumentCategory,
  IngestionStatus,
  PagedResult,
  PagedQuery,
  DocumentCheckoutDto,
} from "@/types/api";

export interface DocumentListQuery extends PagedQuery {
  category?: DocumentCategory;
  folderId?: string;
  folderIdRecursive?: string;
  entityType?: string;
  entityId?: string;
  tags?: string;
  ingestionStatus?: IngestionStatus;
  uploadedBy?: string;
  dateFrom?: string;
  dateTo?: string;
  fileType?: string;
  minSize?: number;
  maxSize?: number;
  /** Document Archive v1.0 — default ActiveOnly (omitted from request). */
  archiveScope?: ArchiveScope;
}

export interface UploadDocumentInput {
  title: string;
  category: DocumentCategory;
  folderId: string;
  file: File;
  description?: string;
  tags?: string;
  autoIngest?: boolean;
}

export interface UpdateMetadataInput {
  title: string;
  description?: string | null;
  category: DocumentCategory;
  tags?: string | null;
}

export const documentsApi = {
  list: (query?: DocumentListQuery) =>
    api.get<PagedResult<DocumentDto>>("/documents", query),
  get: (id: string) => api.get<DocumentDto>(`/documents/${id}`),
  download: (id: string) =>
    api.get<{ sasUrl: string; expiresAt: string; isPdf: boolean }>(`/documents/${id}/download`),
  preview: (id: string) =>
    api.get<{ sasUrl: string; expiresAt: string; isPdf: boolean }>(`/documents/${id}/preview`),
  versions: (id: string) => api.get<DocumentDto[]>(`/documents/${id}/versions`),
  upload: (input: UploadDocumentInput) => {
    const form = new FormData();
    form.append("title", input.title);
    form.append("category", input.category);
    form.append("folderId", input.folderId);
    if (input.description) form.append("description", input.description);
    if (input.tags) form.append("tags", input.tags);
    form.append("autoIngest", String(Boolean(input.autoIngest)));
    form.append("file", input.file);
    return api.upload<DocumentDto>("/documents", form);
  },
  replace: (id: string, file: File) => {
    const form = new FormData();
    form.append("file", file);
    return api.upload<DocumentDto>(`/documents/${id}/replace`, form);
  },
  updateMetadata: (id: string, input: UpdateMetadataInput) =>
    api.put<DocumentDto>(`/documents/${id}`, input),
  // Move a single document into any folder the caller can access, in any
  // record — cross-record moves are permitted. The server rejects only an
  // archived target, or one the caller can't see (reported as not-found so a
  // restricted folder's existence isn't disclosed).
  move: (id: string, targetFolderId: string) =>
    api.put<DocumentDto>(`/documents/${id}/move`, { targetFolderId }),
  delete: (id: string) => api.delete<void>(`/documents/${id}`),

  // Bulk operations
  bulkMove: (documentIds: string[], targetFolderId: string) =>
    api.post<number>("/documents/bulk-move", { documentIds, targetFolderId }),
  bulkDelete: (documentIds: string[]) =>
    api.post<number>("/documents/bulk-delete", { documentIds }),
  bulkTag: (documentIds: string[], addTags?: string, removeTags?: string) =>
    api.post<number>("/documents/bulk-tag", { documentIds, addTags, removeTags }),
  bulkDownload: (documentIds: string[]) =>
    api.post<Array<{ sasUrl: string; expiresAt: string }>>(
      "/documents/bulk-download",
      { documentIds },
    ),

  // Checkout / checkin
  checkout: (id: string, notes?: string) =>
    api.post<DocumentCheckoutDto>(`/documents/${id}/checkout`, { notes }),
  checkin: (id: string) => api.post<void>(`/documents/${id}/checkin`),

  // Ingestion
  ingest: (id: string) => api.post<void>(`/documents/${id}/ingest`),
  bulkIngest: (documentIds: string[]) =>
    api.post<number>("/documents/bulk-ingest", { documentIds }),
  ingestionStatus: (id: string) =>
    api.get<IngestionStatus>(`/documents/${id}/ingestion-status`),

  // Archive (Document Archive v1.0)
  archive: (id: string, reason?: string) =>
    api.post<DocumentDto>(`/documents/${id}/archive`, { reason: reason ?? null }),
  unarchive: (id: string) =>
    api.post<DocumentDto>(`/documents/${id}/unarchive`),
  archiveBulk: (documentIds: string[], reason?: string) =>
    api.post<number>("/documents/archive-bulk", { documentIds, reason: reason ?? null }),
};
